import { endpoint } from "@uri/typed-api";
import { z } from "zod/v4";

const whatsappNumberPattern = /^\d{10,15}(@(c\.us|g\.us|lid))?$/;

const whatsappNumber = z.string().refine(
  (val) => whatsappNumberPattern.test(val),
  "Must be a valid WhatsApp number (10-15 digits, optionally ending with @c.us, @g.us, or @lid)",
);

export const apiDefinition = {
  clearCredentials: endpoint({
    authRequired: false,
    input: z.object({ phoneNumber: z.string(), token: z.string() }),
    output: z.object({
      success: z.boolean().optional(),
      error: z.string().optional(),
    }),
  }),
  setWebhook: endpoint({
    authRequired: false,
    input: z.object({
      phoneNumber: z.string(),
      url: z.string().or(z.null()),
      token: z.string(),
    }),
    output: z.object({}),
  }),
  sendMessage: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: whatsappNumber,
      toNumber: whatsappNumber,
      message: z.string(),
      linkPreview: z.boolean(),
      token: z.string(),
    }),
    output: z.object({ messageId: z.string() }),
  }),
  sendMedia: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: whatsappNumber,
      toNumber: whatsappNumber,
      base64: z.string(),
      type: z.enum(["image", "video", "document"]),
      mimeType: z.string().optional(),
      filename: z.string().optional(),
      caption: z.string().optional(),
      token: z.string(),
    }),
    output: z.object({ messageId: z.string() }),
  }),
  reconnect: endpoint({
    authRequired: false,
    input: z.object({ number: z.string(), token: z.string() }),
    output: z.object({}),
  }),
  triggerCodeLogin: endpoint({
    authRequired: false,
    input: z.object({ number: z.string(), token: z.string() }),
    output: z.object({}),
  }),
  addUserToWhatsAppGroup: endpoint({
    authRequired: false,
    input: z.object({
      groupId: z.string(),
      userId: z.string(),
      fromNumber: z.string(),
      token: z.string(),
    }),
    output: z.object({ success: z.boolean() }),
  }),
  getAdminWhatsappGroups: endpoint({
    authRequired: false,
    input: z.object({ fromNumber: z.string(), token: z.string() }),
    output: z.object({
      groups: z.array(z.object({ id: z.string(), name: z.string() })),
    }),
  }),
  getWhatsappJoinRequests: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      groupId: z.string(),
      token: z.string(),
    }),
    output: z.union([
      z.object({ success: z.literal(false) }),
      z.object({
        success: z.literal(true),
        requests: z.array(z.object({
          requesterId: z.string(),
          addedById: z.string().optional(),
          parentGroupId: z.string().optional(),
          method: z.string(),
          timestamp: z.number(),
        })),
      }),
    ]),
  }),
  getCommonWhatsappGroups: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      userId: z.string(),
      token: z.string(),
    }),
    output: z.object({
      groups: z.array(z.object({ id: z.string(), name: z.string() })),
    }),
  }),
  approveWhatsappJoinRequests: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      groupId: z.string(),
      requesterIds: z.array(z.string()).nullable().optional(),
      token: z.string(),
    }),
    output: z.object({
      results: z.array(z.object({
        requesterId: z.string(),
        error: z.number().nullable(),
        message: z.string(),
      })),
    }),
  }),
  rejectWhatsappJoinRequests: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      groupId: z.string(),
      requesterIds: z.array(z.string()),
      token: z.string(),
    }),
    output: z.object({
      results: z.array(z.object({
        requesterId: z.string(),
        error: z.number().nullable(),
        message: z.string(),
      })),
    }),
  }),
  sendTypingIndication: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      chatId: z.string(),
      durationMs: z.number().optional(),
      token: z.string(),
    }),
    output: z.object({ success: z.boolean() }),
  }),
  stopTypingIndication: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      chatId: z.string(),
      token: z.string(),
    }),
    output: z.object({ success: z.boolean() }),
  }),
  convertLidToNumber: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      lid: z.string(),
      token: z.string(),
    }),
    output: z.object({ number: z.string().nullable() }),
  }),
  changeWhatsappAccountName: endpoint({
    authRequired: false,
    input: z.object({
      phoneNumber: z.string(),
      name: z.string(),
      token: z.string(),
    }),
    output: z.object({}),
  }),
  changeWhatsappAccountAvatar: endpoint({
    authRequired: false,
    input: z.object({
      phoneNumber: z.string(),
      imageBase64: z.string(),
      token: z.string(),
    }),
    output: z.object({}),
  }),
  getWhatsappGroupDetails: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      groupLink: z.string(),
      token: z.string(),
    }),
    output: z.union([
      z.object({
        success: z.literal(true),
        id: z.string(),
        subject: z.string(),
        desc: z.string(),
        size: z.number(),
        owner: z.string().optional(),
        isInGroup: z.boolean(),
        announce: z.boolean(),
        restrict: z.boolean(),
        creation: z.number(),
        suspended: z.boolean(),
        participants: z.array(z.object({
          id: z.string(),
          isAdmin: z.boolean(),
          isSuperAdmin: z.boolean(),
        })),
      }),
      z.object({
        success: z.literal(false),
        error: z.string(),
      }),
    ]),
  }),
} as const;

export type Api = typeof apiDefinition;
